import { useEffect, useState } from "react";
import {
  Box,
  Icon,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Chip,
  useTheme,
} from "@mui/material";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useNavigate } from "react-router-dom";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { baseApiService } from "../../shared/services/api/axios-config/axios-config";
import { FiltroMovimentacoesModal } from "../../shared/components/ModalFiltros/modal-filtros";
import { entradaDeNotasCompraService } from "./service/entrada-de-notas-compra.service";
import { ModalImportacaoNota } from "../../shared/components/ModalImportarNota/modal-importar-nota";
import { toast } from "react-toastify";

interface Work {
  id: string;
  workName: string;
}

interface Client {
  id: string;
  fantasyName?: string;
  corporateName?: string;
  fullName?: string;
}

interface Supplier {
  id: string;
  corporateName?: string;
  fantasyName?: string;
  fullName?: string;
}

interface NotaEntrada {
  id: string;
  invoiceNumber: string;
  series: string;
  issueDate: string;
  totalValue: number;
  workId: string;
  clientId: string;
  supplierId: string;
  work?: Work | null;
  client?: Client | null;
  supplier?: Supplier | null;
  status?: string;
}

export function ListagemNotasDeCompras() {
  const [notas, setNotas] = useState<NotaEntrada[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Estados do modal de importação
  const [modalImportacaoOpen, setModalImportacaoOpen] = useState(false);

  // Filtros do modal
  const [openFiltro, setOpenFiltro] = useState(false);
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const dateInicio = new Date(
    firstDay.getTime() - firstDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);

  const [dateStart, setDateStart] = useState(dateInicio);
  const [dateEnd, setDateEnd] = useState(today);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [workIds, setWorkIds] = useState<string[]>([]);
  const [clientesInput, setClientesInput] = useState<any[]>([]);
  const [obras, setObras] = useState<any[]>([]);
  const [query, setQuery] = useState<string>();

  const buscaDebounce = useDebounce(busca, 500);
  const theme = useTheme();
  const navigate = useNavigate();

  // Carregar clientes e obras para os filtros
  useEffect(() => {
    const fetchInitialData = async () => {
      const clientesData = await baseApiService.get("/client/select");
      const clientes = clientesData.data.data || [];
      setClientesInput(clientes);

      const obrasData = await baseApiService.get("/work/select");
      setObras(obrasData.data.data || []);
    };
    fetchInitialData();
  }, []);

  // Funções do modal de importação
  const handleImportSuccess = (importedData: any) => {
    toast.success("Nota fiscal importada e salva com sucesso!");
    // Recarrega a lista após importação bem-sucedida
    carregarNotas(query || buscaDebounce, page);
  };

  const handleImportError = (error: any) => {
    console.error("Erro na importação:", error);
    // O toast de erro já é exibido no modal
  };

  // Função para montar a query do filtro
  const handleFiltrar = () => {
    let query = "";

    if (dateStart && dateEnd) {
      query += `campo_pesquisa=issue_date&tipo_pesquisa=entre&valor_pesquisa=${dateStart},${dateEnd}&`;
    }
    if (clientIds.length) {
      query += `campo_pesquisa=id_client&tipo_pesquisa=in&valor_pesquisa=${clientIds.join(
        ","
      )}&`;
    }
    if (workIds.length) {
      query += `campo_pesquisa=id_work&tipo_pesquisa=in&valor_pesquisa=${workIds.join(
        ","
      )}&`;
    }
    query = query.replace(/&$/, "");
    setQuery(query);
    carregarNotas(query);
    setOpenFiltro(false);
  };

  const carregarNotas = async (
    query: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      setIsLoading(true);
      const result = await entradaDeNotasCompraService.findAll(
        query,
        pagina,
        limite
      );
      // Ajuste para aceitar cliente/obra/supplier nulos
      setNotas(
        (result.items || []).map((nota: any) => ({
          ...nota,
          work: nota.work ?? null,
          client: nota.client ?? null,
          supplier: nota.supplier ?? null,
        }))
      );
      setTotalCount(result?.meta?.totalItems || 0);
    } catch (error) {
      setNotas([]);
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
    carregarNotas(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarNotas(query || buscaDebounce, newPage);
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm(
        "Tem certeza que deseja deletar essa entrada de nota? Essa ação não pode ser desfeita."
      )
    ) {
      try {
        await baseApiService.delete(`/input-invoice/${id}`);
        toast.success("Entrada de nota deletada com sucesso!");
        // Recarrega a lista após exclusão
        carregarNotas(query || buscaDebounce, page);
      } catch (error) {
        toastError(error);
      }
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarNotas(query || buscaDebounce, 0, newLimit);
  };

  return (
    <BaseLayout
      title="Listagem de Notas de Compras"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por número ou cliente"}
          textoBotaoNovo="Adicionar Nota"
          mostrarInputBusca
          mostraBotaoFiltro={true}
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() =>
            navigate("/financeiro/entrada-notas-compra/adicionar")
          }
          aoClicarBotaoFiltro={() => setOpenFiltro(true)}
          mostrarBotaoAdicional={true}
          textoBotaoAdicional="Importar Nota via XML"
          aoClicarBotaoAdicional={() => setModalImportacaoOpen(true)}
        />
      }
    >
      {/* Modal de Importação */}
      <ModalImportacaoNota
        open={modalImportacaoOpen}
        onClose={() => setModalImportacaoOpen(false)}
        title="Importar e Criar Nota Fiscal de Compra"
        acceptedFormats=".xml"
        importEndpoint="/input-invoice/import"
        helperText="Selecione um arquivo XML da nota fiscal para importar e criar automaticamente uma nova nota de compra"
        onImportSuccess={handleImportSuccess}
        onImportError={handleImportError}
      />

      {/* Modal de Filtros */}
      <FiltroMovimentacoesModal
        open={openFiltro}
        onClose={() => setOpenFiltro(false)}
        dateStart={dateStart}
        setDateStart={setDateStart}
        dateEnd={dateEnd}
        setDateEnd={setDateEnd}
        clientesInput={clientesInput}
        clientIds={clientIds}
        setClientIds={setClientIds}
        obras={obras}
        workIds={workIds}
        setWorkIds={setWorkIds}
        onFiltrar={handleFiltrar}
      />

      <Box display="flex" flexDirection="column" paddingBottom="40px">
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />

        <TabelaDaListagem
          ariaLabel="tabela de notas"
          headTable={[
            "Número da Nota",
            "Série",
            "Obra",
            "Cliente",
            "Fornecedor",
            "Data de Emissão",
            "Valor Total",
            "Status",
            "Ações",
          ]}
          columnWidths={[120, 80, 200, 200, 200, 150, 120, 120, 120]}
          tableBody={
            !isLoading && notas.length > 0 ? (
              notas.map((nota) => (
                <TableRow hover key={nota.id}>
                  <TableCell sx={{ minWidth: 120 }}>
                    {nota.invoiceNumber}
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }}>{nota.series}</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    {nota.work?.workName || "-"}
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    {nota.client?.fantasyName ||
                      nota.client?.corporateName ||
                      nota.client?.fullName ||
                      "-"}
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    {nota.supplier?.fantasyName ||
                      nota.supplier?.corporateName ||
                      nota.supplier?.fullName ||
                      "-"}
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {nota.issueDate
                      ? new Date(nota.issueDate).toLocaleDateString("pt-BR")
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    {nota.totalValue != null
                      ? new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(Number(nota.totalValue))
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Chip
                      label={
                        nota.status === "generated"
                          ? "Gerada"
                          : nota.status === "canceled"
                          ? "Cancelada"
                          : "-"
                      }
                      color={
                        nota.status === "generated"
                          ? "success"
                          : nota.status === "canceled"
                          ? "error"
                          : "default"
                      }
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Tooltip title="Editar nota">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            `/financeiro/entrada-notas-compra/detalhe/${nota.id}`
                          )
                        }
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar nota">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(nota.id)}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {isLoading
                    ? "Carregando..."
                    : "Nenhuma entrada de nota encontrada :("}
                </TableCell>
              </TableRow>
            )
          }
        />
      </Box>
    </BaseLayout>
  );
}
